import { GAMES } from "./games";

/**
 * 뉴스레터 카테고리.
 *
 * 회사 전체 이야기는 "회사소식", 게임 하나에 대한 이야기는 그 게임 카테고리에 싣는다.
 * id 는 URL 에 그대로 쓰인다 (/ko/newsletter/category/student-maker).
 * 한 번 정한 id 는 바꾸지 않는다 — 공유된 링크가 깨진다. 이름은 label 에서 바꾼다.
 *
 * 게임 카테고리는 games.js 의 색과 로고를 그대로 쓴다. 목록 탭의 강조색, 카테고리
 * 머리글의 로고, 그 카테고리 글 상세 페이지의 링크 색이 모두 그 값을 따른다.
 *
 * 배열 순서가 목록 탭 순서다.
 */

export const DEFAULT_CATEGORY = "company";

export const CATEGORIES = [
  {
    id: "company",
    label: { ko: "회사소식", en: "Company News" },
    description: {
      ko: "바삭한소프트의 방향과 계획, 그리고 만드는 사람들의 이야기.",
      en: "Where Basakansoft is heading, and the people making it.",
    },
    aliases: ["회사 소식", "회사", "공지", "company", "news", "company news"],
  },
  {
    id: "job-seeker",
    game: GAMES.jobSeeker,
    label: { ko: "취준생 키우기", en: "Hire Me" },
    aliases: ["jobSeeker", "취키"],
  },
  {
    id: "student-maker",
    game: GAMES.studentMaker,
    label: { ko: "수험생 키우기", en: "Korean Student Simulator" },
    aliases: ["studentMaker", "수키", "student maker"],
  },
  {
    id: "galaxy",
    game: GAMES.galaxy,
    label: { ko: "은하원정대", en: "Galaxy Expedition" },
    aliases: ["은정대"],
  },
  {
    id: "love-revolution",
    game: GAMES.yeonae,
    label: { ko: "연애혁명 키우기", en: "Love Revolution Maker" },
    aliases: ["yeonae", "연애혁명", "연혁키"],
  },
];

const GAME_DESCRIPTION = {
  ko: (name) => `${name}의 업데이트와 이벤트, 개발 이야기.`,
  en: (name) => `Updates, events and development notes from ${name}.`,
};

// 대소문자·공백·하이픈 차이는 무시하고 비교한다. Notion 에서 "수험생키우기" 처럼
// 띄어쓰기 없이 적어도 찾아지게.
function normalizeKey(value) {
  return String(value).toLowerCase().replace(/[\s_-]+/g, "");
}

const lookup = new Map();
for (const category of CATEGORIES) {
  const names = [
    category.id,
    category.label.ko,
    category.label.en,
    ...(category.aliases || []),
  ];
  for (const name of names) {
    lookup.set(normalizeKey(name), category);
  }
}

/** id 로 카테고리를 찾는다. 없으면 null. */
export function getCategory(id) {
  return CATEGORIES.find((category) => category.id === id) || null;
}

/**
 * 원고에 적힌 값(id, 한글/영문 이름, 별칭)을 카테고리 id 로 바꾼다.
 * 비어 있으면 기본 카테고리, 알 수 없는 값이면 null.
 */
export function resolveCategory(value) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return DEFAULT_CATEGORY;
  }
  const category = lookup.get(normalizeKey(value));
  return category ? category.id : null;
}

export function categoryLabel(category, lang = "ko") {
  return category.label[lang] || category.label.ko;
}

export function categoryDescription(category, lang = "ko") {
  if (category.description) {
    return category.description[lang] || category.description.ko;
  }
  const describe = GAME_DESCRIPTION[lang] || GAME_DESCRIPTION.ko;
  return describe(categoryLabel(category, lang));
}

// 지면 브랜드 색(theme.js 의 --nl-brand)과 같은 값. 회사소식은 브랜드 색을 쓴다.
const BRAND_ACCENT = "#b58200";

/** 카테고리의 강조색. 게임이면 그 게임 색, 아니면 브랜드 색. */
export function categoryAccent(category) {
  return category && category.game ? category.game.accent : BRAND_ACCENT;
}
